import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '@shared/config/routes';

import { refreshSession } from '../api/refresh-session';
import { fetchSessionInfo } from '../api/user-info';
import { useAuth } from './use-auth';

const AUTH_REFRESH_LEEWAY_MS = 10_000;
const AUTH_REFRESH_MIN_DELAY_MS = 1000;

const getNextSessionRefreshDelayMs = (expiresInSec?: number) => {
	if (!expiresInSec || expiresInSec <= 0) {
		return AUTH_REFRESH_MIN_DELAY_MS;
	}

	const ttlMs = Math.floor(expiresInSec * 1000);
	return Math.max(AUTH_REFRESH_MIN_DELAY_MS, ttlMs - AUTH_REFRESH_LEEWAY_MS);
};

/**
 * Хук для проверки авторизации и обработки возврата с SSO.
 * Возвращает флаг, что проверка ещё выполняется.
 */
export const useAuthGuard = () => {
	const navigate = useNavigate();
	const { setAuth, resetAuth } = useAuth();
	const [isChecking, setIsChecking] = useState(true);

	const handleUnauthorized = useCallback(async () => {
		resetAuth();
		await navigate(`${ROUTES.LOGIN}${window.location.search}`, { replace: true });
	}, [navigate, resetAuth]);

	const loadSessionInfo = useCallback(async () => {
		const sessionInfo = await fetchSessionInfo();
		if (!sessionInfo.isOkStatus || !sessionInfo.data) {
			throw new Error(`Unexpected /user_info status: ${sessionInfo.response.status}`);
		}

		setAuth(sessionInfo.data.user);
		return sessionInfo.data.expiresIn;
	}, [setAuth]);

	useEffect(() => {
		let timerId: number | null = null;
		let isCancelled = false;

		const clearRefreshTimer = () => {
			if (timerId !== null) {
				window.clearTimeout(timerId);
				timerId = null;
			}
		};

		const scheduleNextRefresh = (expiresInSec?: number) => {
			if (isCancelled) return;
			clearRefreshTimer();

			const delayMs = getNextSessionRefreshDelayMs(expiresInSec);
			timerId = window.setTimeout(() => {
				refreshAndReschedule().catch(() => {});
			}, delayMs);
		};

		const refreshAndReschedule = async () => {
			if (isCancelled) return;

			try {
				const response = await refreshSession();
				if (!response.isOkStatus) {
					throw new Error(`Unexpected /refresh status: ${response.response.status}`);
				}

				const expiresInSec = await loadSessionInfo();
				scheduleNextRefresh(expiresInSec);
			} catch {
				clearRefreshTimer();
				if (!isCancelled) {
					await handleUnauthorized();
				}
			}
		};

		const initializeSessionGuard = async () => {
			setIsChecking(true);
			try {
				const expiresInSec = await loadSessionInfo();
				if (isCancelled) return;

				setIsChecking(false);
				scheduleNextRefresh(expiresInSec);
			} catch {
				if (!isCancelled) {
					setIsChecking(false);
					await handleUnauthorized();
				}
			}
		};

		initializeSessionGuard().catch(() => {});

		return () => {
			isCancelled = true;
			clearRefreshTimer();
		};
	}, [handleUnauthorized, loadSessionInfo]);

	return { isChecking };
};
